import { toSingular } from './plural';
import ApiError from './api-error';
import fetch from 'node-fetch';
import Jsona from 'jsona';
import { realUnset } from './unset';
import { convertEmptyStringToNull, isEmptyObject, propertyExists, removeEmptyKeys } from './api-utils';
import { trackPromise } from 'react-promise-tracker';
import get from 'get-value';
import set from 'set-value';
import { getApiDomain } from '../shared-variables';
import { getAccessToken } from '../auth-utils';

/**
 * This class handles making requests to the API. All you really need to care about are:
 * API.request() - for GET/DELETE requests
 * API.create() - for POST requests
 * API.update() - for PUT requests
 * API.submit() - for dashboard PUT/POST requests
 */
export default class API {
	/**
	 * Makes a request to the API.
	 * @param {string} url
	 * @param {object} options
	 * @param {string} options.method - optional
	 * @param {object} options.headers - optional
	 * @param {object} options.body - optional
	 * @param {object} options.files - optional
	 * @return {Promise}
	 */
	static request( url, options = {} ) {
		const domain = getApiDomain();
		if ( !domain ) {
			return new Promise( () => {
				throw new Error( 'API URL is not set.' );
			} );
		}

		let context = null;
		if ( options.context ) {
			context = options.context;
			delete options.context;
		}

		options.headers = API.getHeaders( options, context );
		delete options.headers.bearer;
		options.body = API.formatBody( options );
		delete options.files;

		return trackPromise( fetch( `${domain}/${url}`, options )
			.then( response => {
				if ( !response.ok ) {
					return response.json().then( data => { throw new ApiError( data, response.status ); } );
				} else if ( response.status === 204 ) {
					return {};
				}
				return response.json();
			} )
			.then( response => {
				if ( response.data ) {
					const meta = response.meta;
					response = new Jsona().deserialize( response );

					// Jsona doesn't include top-level meta.
					if ( meta ) {
						if ( Array.isArray( response ) ) {
							response = {
								data: response,
								meta,
							};
						} else {
							response.meta = meta;
						}
					}
				}
				return response;
			} ) );
	}

	/**
	 * Creates a new record.
	 * @param {string} endpoint - eg. 'posts'
	 * @param {object} body - eg. { name: 'Foo', user: { id: 1, type: 'user' }, relationshipNames: ['user'] }
	 * @param {object[File]} files - optional, eg. { 'data.meta.attachments.image': document.getElementById( 'image' ).files[0] }
	 * @return {Promise}
	 */
	static create( endpoint, reqBody, files = null ) {
		let body = reqBody;
		if ( body.singular ) {
			body.type = body.singular;
			delete body.singular;
		} else {
			body.type = toSingular( endpoint.replace( /\?.+/, '' ) );
		}
		body = new Jsona().serialize( { stuff: body } );

		const options = {
			method: 'POST',
			body,
		};
		if ( files ) {
			options.files = files;
		}

		return API.request( endpoint, options );
	}

	/**
	 * Updates an existing record.
	 * @param {string} endpoint - eg. 'posts'
	 * @param {string} id - eg. 1
	 * @param {object} body - eg. { name: 'Foo', user: { id: 1, type: 'user' }, relationshipNames: ['user'] }
	 * @param {object[File]} files - optional, eg. { 'data.meta.attachments.image': document.getElementById( 'image' ).files[0] }
	 * @return {Promise}
	 */
	static update( endpoint, id, body, files = null, append = null ) {
		body.type = toSingular( endpoint.replace( /\?.+/, '' ) );
		body.id = id;

		const included = body.included;
		delete body.included;
		const meta = body.meta;
		delete body.meta;

		body = new Jsona().serialize( { stuff: body } );

		if ( included ) {
			body.included = included;
		}
		if ( meta ) {
			body.data.meta = meta;
		}

		const options = {
			method: 'PUT',
			body,
		};
		if ( files ) {
			options.files = files;
		}

		return API.request( `${endpoint}/${id}${append ? append : ''}`, options );
	}

	/**
	 * Deletes an existing record.
	 * @param {string} endpoint - eg. 'posts'
	 * @param {string} id - eg. 1
	 * @return {Promise}
	 */
	static delete( endpoint, id ) {
		return API.request( `${endpoint}/${id}`, { method: 'DELETE' } );
	}

	/**
	 * Submits form values to the API.
	 * @param {object} args
	 * @param {string} args.endpoint - required, eg. 'posts'
	 * @param {string} args.id - optional, only needed if updating an existing record
	 * @param {string} args.type - required, eg. 'user'
	 * @param {object} args.values - required, eg. { name: 'Foo', user: { id: 1, type: 'user' } }
	 * @param {array[string]} args.files - optional, eg. ['meta.attachments.image']
	 * @param {array[string]} args.relationshipNames - optional, eg. ['user']
	 * @return {Promise}
	 */
	static submit( args ) {
		const url = API.getFormUrl( args );
		const files = API.getFormFiles( args );
		const options = {
			method: API.getFormMethod( args.id ),
			body: API.getFormBody( args, files ),
			files: files,
		};
		if ( isEmptyObject( options.body ) ) {
			return new Promise( () => {
				throw new ApiError( { errors: [{ title: 'No changes made.' }] } );
			} );
		}
		return API.request( url, options );
	}

	/**
	 * Returns the headers for the request.
	 * @param {object} options
	 * @param {object} options.headers - optional
	 * @param {object} options.files - optional
	 * @param {object} context
	 * @return {object}
	 */
	static getHeaders( options ) {
		let headers = options.headers || {};

		const token = getAccessToken();
		headers.Authorization = `Bearer ${token}`;

		// Authentication for client-side requests.
		if ( typeof document !== 'undefined' ) {
			const csrfToken = document.querySelector( '[name="X-CSRF-Token"]' );
			if ( csrfToken ) {
				headers['X-CSRF-Token'] = csrfToken.getAttribute( 'content' );
			}
		}

		if ( !options.files ) {
			// When there are files, the browser will set the Content-Type:
			// https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
			headers['Content-Type'] = 'application/vnd.api+json';
		}

		return headers;
	}

	/**
	 * Formats the request body as JSON or form data.
	 * @param {object} options.body - optional
	 * @param {object} options.files - optional
	 * @return {string, FormData}
	 */
	static formatBody( { body, files } ) {
		let output = null;

		if ( body ) {
			output = JSON.stringify( body );
		}

		if ( files ) {
			const formData = new FormData();
			if ( output ) {
				formData.append( 'data', output );
			}
			Object.keys( files ).forEach( key => {
				const value = files[key];
				formData.append( key, value );
			} );
			output = formData;
		}

		return output;
	}

	/**
	 * Returns the URL for the request.
	 * @param {string} options.id - optional
	 * @param {array} options.include - optional
	 * @param {string} options.endpoint - required
	 * @return {string}
	 */
	static getFormUrl( { id, include, endpoint } ) {
		let url;
		if ( id ) {
			url = `${endpoint}/${id}`;
		} else {
			url = endpoint;
		}

		if ( include && include.length > 0 ) {
			url += `?include=${include.join( ',' )}`;
		}

		return url;
	}

	/**
	 * Returns the method for the request.
	 * @param {string} id
	 * @return {string}
	 */
	static getFormMethod( id ) {
		return id ? 'PUT' : 'POST';
	}

	/**
	 * Returns the body for the request.
	 * @param {string} options.id - optional
	 * @param {array} options.relationshipNames - optional
	 * @param {object} options.values - required
	 * @param {string} options.type - required
	 * @param {object} files
	 * @return {object}
	 */
	static getFormBody( { id, relationshipNames, values, type }, files ) {
		let output = values || {};

		API.removeUnchangedFields( output );
		removeEmptyKeys( output );
		convertEmptyStringToNull( output );

		if ( !isEmptyObject( output ) || ( files && !isEmptyObject( files ) ) ) {
			output.type = type;
			output.id = id;
			let included = [];

			if ( relationshipNames ) {
				output.relationshipNames = relationshipNames;

				relationshipNames.forEach( ( relationshipName ) => {
					if ( propertyExists( output, relationshipName ) && output[relationshipName] && !get( output, `${relationshipName}.id` ) ) {
						// If this is a has-many relationship, convert the object to an array.
						output[relationshipName] = Object.values( output[relationshipName] );
						output[relationshipName].forEach( rel => {
							if ( propertyExists( rel, 'attributes' ) && !isEmptyObject( rel.attributes ) ) {
								included.push( rel );
							}
						} );
					}
				} );
			}

			output = new Jsona().serialize( { stuff: output } );

			if ( !output.data.id ) {
				delete ( output.data.id );
			}
			if ( get( output, 'data.attributes.meta' ) ) {
				set( output, 'data.meta', get( output, 'data.attributes.meta' ) );
				realUnset( output, 'data.attributes.meta' );
			}

			if ( get( output, 'data.attributes.included' ) ) {
				set( output, 'included', get( output, 'data.attributes.included' ) );
				realUnset( output, 'data.attributes.included' );
			}

			if ( isEmptyObject( output.data.attributes ) ) {
				delete ( output.data.attributes );
			}

			if ( included.length > 0 ) {
				output.included = included;
			}
		}

		return output;
	}

	/**
	 * Removes fields that have not been changed.
	 * @param {object} values
	 */
	static removeUnchangedFields( values ) {
		// Hidden <Field>s will not be removed; if you don't want a hidden field to be sent, remove it from the DOM.
		// File fields will always be removed; files are sent separately.
		const inputs = document.querySelectorAll( '.is-pristine:not([type="hidden"]),[type="file"]' );
		inputs.forEach( input => {
			realUnset( values, input.name );
		} );
	}

	/**
	 * Returns the files for the request.
	 * @param {array[string]} files
	 * @return {object}
	 */
	static getFormFiles( args ) {
		const { alternateImage, files } = args;
		if ( !files ) {
			return null;
		}
		let output = {};
		let hasFiles = false;

		files.forEach( attribute => {
			const elem = document.querySelector( `[type="file"][name="${attribute}"]` );
			if ( elem && elem.files && elem.files.length > 0 ) {
				output[`data.${attribute}`] = elem.files[0];
				hasFiles = true;
			}
		} );

		if ( args.type === 'profile' && alternateImage !== null ) {
			output['data.meta.attachments.cropped_image'] = alternateImage;
			hasFiles = true;
		}

		if ( !hasFiles ) {
			return null;
		}
		return output;
	}
}
